import axios from "axios";
import authHeader from "./auth-header";
import { BASE_URL } from "./config";

const safeRequest = (promise) => promise.then((res) => res).catch(() => null);

export const getAvailableWikiData = async (company_name) => {
  try {
    const results = await Promise.allSettled([
      axios.post(
        BASE_URL + "wiki/getCompanyProfileIcon",
        { company_name },
        { headers: authHeader() },
      ),
      axios.post(
        BASE_URL + "wiki/getCompanyEmployeesIcon",
        { company_name },
        { headers: authHeader() },
      ),
      axios.post(
        BASE_URL + "wiki/getCourtCasesIcon",
        { company_name },
        { headers: authHeader() },
      ),
      axios.post(
        BASE_URL + "wiki/getNeighborsIcon",
        { company_name },
        { headers: authHeader() },
      ),
      axios.post(
        BASE_URL + "wiki/getGovAwardsIcon",
        { company_name },
        { headers: authHeader() },
      ),
      axios.post(
        BASE_URL + "wiki/getDomainCertsIcon",
        { company_name },
        { headers: authHeader() },
      ),
      axios.post(
        BASE_URL + "wiki/getBolsIcon",
        { company_name },
        { headers: authHeader() },
      ),
      axios.post(
        BASE_URL + "wiki/getSanctionLookupIcon",
        { company_name },
        { headers: authHeader() },
      ),
      axios.get(BASE_URL + "wiki/getSitemapUrlIcon", {
        params: { company_name },
        headers: authHeader(),
      }),
      axios.post(
        BASE_URL + "wiki/getCrypto",
        { company_name },
        { headers: authHeader() },
      ),
      axios.post(
        BASE_URL + "wiki/getAvailableTargetTypes",
        { company_name },
        { headers: authHeader() },
      ),
    ]);

    // Extract data safely — failed requests become null
    const getData = (index) => {
      const result = results[index];
      if (
        result.status === "fulfilled" &&
        result.value?.data &&
        result.value.data !== "fail"
      ) {
        return result.value.data;
      }
      return null;
    };

    const profile = getData(0);
    const employees = getData(1);
    const cases = getData(2);
    const neighbors = getData(3);
    const gov = getData(4);
    const domainsCerts = getData(5);
    const bols = getData(6);
    const sanctions = getData(7);
    const site = getData(8);
    const crypto = getData(9);
    const reportTypes = getData(10);

    let response_array = [];

    // Overview always gets pushed (with or without data)
    response_array.push({
      sub_section: "Overview",
      ...(profile ? { data: profile } : {}),
    });

    if (employees && Array.isArray(employees) && employees.length > 0) {
      response_array.push({
        sub_section: "Key Employees",
        data: employees,
      });
    }

    if (cases && cases.count > 0) {
      response_array.push({ sub_section: "Court Cases", data: cases });
    }

    if (neighbors && Array.isArray(neighbors) && neighbors.length > 0) {
      response_array.push({ sub_section: "Neighbors", data: neighbors });
    }

    if (gov && gov.count > 0) {
      response_array.push({ sub_section: "Gov Awards", data: gov });
    }

    if (
      domainsCerts &&
      Array.isArray(domainsCerts) &&
      domainsCerts.length > 0
    ) {
      const transformedData = domainsCerts.map((item) => ({
        title: item.name_value,
        value: item.issuer_ca_id,
      }));
      response_array.push({ sub_section: "Domains", data: transformedData });
    }

    if (
      bols &&
      Array.isArray(bols) &&
      bols.length > 0 &&
      bols.find((item) => item.bol_count)?.bol_count > 0
    ) {
      response_array.push({ sub_section: "BOLs", data: bols });
    }

    if (sanctions && sanctions.count > 0) {
      response_array.push({ sub_section: "Sanctions", data: sanctions });
    }

    if (site && site.count > 0) {
      response_array.push({ sub_section: "Site Map", data: site });
    }

    if (crypto && Array.isArray(crypto) && crypto.length > 0) {
      response_array.push({ sub_section: "Crypto", data: crypto });
    }

    if (reportTypes && Array.isArray(reportTypes) && reportTypes.length > 0) {
      response_array.push({
        sub_section: "Reports",
        data: { count: reportTypes.length },
      });
    }

    return response_array;
  } catch (err) {
    console.error("Error fetching wiki data:", err);
    return [];
  }
};

export const getCompanyEmployees = async (company_name) => {
  try {
    const response = await axios.post(
      `${BASE_URL}wiki/getCompanyEmployees`,
      { company_name },
      { headers: authHeader() },
    );
    return response;
  } catch (error) {
    console.error("Error fetching company employees:", error);
    return { data: [] };
  }
};

export const getCourtCases = async (company_name) => {
  try {
    const response = await axios.post(
      `${BASE_URL}wiki/getCourtCases`,
      { company_name },
      { headers: authHeader() },
    );
    return response;
  } catch (error) {
    console.error("Error fetching company court cases:", error);
    return { data: [] };
  }
};

export const getNeighbors = async (company_name) => {
  try {
    const response = await axios.post(
      `${BASE_URL}wiki/getNeighbors`,
      { company_name },
      { headers: authHeader() },
    );
    return response;
  } catch (error) {
    console.error("Error fetching company getNeighbors:", error);
    return { data: [] };
  }
};

export const getDomainCerts = async (company_name) => {
  try {
    const response = await axios.post(
      `${BASE_URL}wiki/getDomainCerts`,
      { company_name },
      { headers: authHeader() },
    );
    return response;
  } catch (error) {
    console.error("Error fetching company getDomainCerts:", error);
    return { data: [] };
  }
};

export const getSanctions = async (company_name) => {
  try {
    const response = await axios.post(
      `${BASE_URL}wiki/getSanctionLookup`,
      { company_name },
      { headers: authHeader() },
    );
    return response;
  } catch (error) {
    console.error("Error fetching company getSanctions:", error);
    return { data: [] };
  }
};

export const getCrypto = async (company_name) => {
  try {
    const response = await axios.post(
      `${BASE_URL}wiki/getCrypto`,
      { company_name },
      { headers: authHeader() },
    );
    return response;
  } catch (error) {
    console.error("Error fetching company getCrypto:", error);
    return { data: [] };
  }
};

export const getAvailableTargetTypes = async (company_name) => {
  try {
    const response = await axios.post(
      `${BASE_URL}wiki/getAvailableTargetTypes`,
      { company_name },
      { headers: authHeader() },
    );
    return response;
  } catch (error) {
    console.error("Error fetching available target types:", error);
    return { data: [] };
  }
};

export const getReports = async (company_name) => {
  try {
    const typesResponse = await getAvailableTargetTypes(company_name);
    const targetTypes = typesResponse?.data || [];
    if (!Array.isArray(targetTypes) || targetTypes.length === 0) {
      return { data: [] };
    }

    const reportPromises = targetTypes.map((target_type) =>
      axios.post(
        `${BASE_URL}wiki/getReports`,
        { company_name, target_type },
        { headers: authHeader() },
      ),
    );

    const results = await Promise.allSettled(reportPromises);
    const allReports = [];

    results.forEach((result, index) => {
      if (result.status === "fulfilled" && Array.isArray(result.value?.data)) {
        const versions = result.value.data;
        if (versions.length > 0) {
          allReports.push({
            ...versions[0],
            target_type: targetTypes[index],
            versions: versions,
          });
        }
      }
    });

    allReports.sort((a, b) => {
      const tsA = a.timestamp || "";
      const tsB = b.timestamp || "";
      if (tsA !== tsB) return tsA > tsB ? -1 : 1;
      const typeA = (a.target_type || "").toLowerCase();
      const typeB = (b.target_type || "").toLowerCase();
      return typeA.localeCompare(typeB);
    });

    return { data: allReports };
  } catch (error) {
    console.error("Error fetching reports:", error);
    return { data: [] };
  }
};
